/**
 * LiveTradeInsights — Mid-Trade Coaching Chips
 *
 * Renders the ranked list of live signals from `computeLiveInsights` as a
 * compact row of color-coded pills inside the LiveTradePanel. Shows the
 * highest-priority 2-3 signals only — the goal is "whisper, don't shout" while
 * the user is in a trade.
 *
 * Signal tone maps to a distinct visual:
 *   - warn    → amber, for patterns like overtrading / tough hour / bad event
 *   - success → emerald, for edges (this setup works at this hour)
 *   - info    → slate, neutral context
 *
 * Pro signals get a subtle purple ring accent to reinforce the tier without
 * needing a separate "PRO" label (which would eat space in a tight panel).
 *
 * Props:
 *   signals        Array from `computeLiveInsights()`
 *   darkMode       bool
 */

import { AlertTriangle, Sparkles, TrendingUp, Info } from 'lucide-react';

const TONE_ICONS = {
  warn: AlertTriangle,
  success: TrendingUp,
  info: Info,
};

const TONE_COLORS = (tone, darkMode, isPro) => {
  const proRing = isPro
    ? (darkMode ? 'ring-1 ring-purple-400/20' : 'ring-1 ring-purple-300/40')
    : '';
  if (tone === 'warn') {
    return darkMode
      ? `bg-amber-500/10 border-amber-400/40 text-amber-200 ${proRing}`
      : `bg-amber-50 border-amber-300 text-amber-800 ${proRing}`;
  }
  if (tone === 'success') {
    return darkMode
      ? `bg-emerald-500/10 border-emerald-400/40 text-emerald-200 ${proRing}`
      : `bg-emerald-50 border-emerald-300 text-emerald-800 ${proRing}`;
  }
  // info — deliberately muted; an info signal shouldn't compete with a warn
  return darkMode
    ? `bg-white/[0.03] border-white/10 text-gray-200 ${proRing}`
    : `bg-gray-50 border-gray-200 text-gray-700 ${proRing}`;
};

const LiveTradeInsights = ({ signals, darkMode = true }) => {
  if (!Array.isArray(signals) || signals.length === 0) return null;

  return (
    <div className="mt-2">
      <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 flex items-center gap-1.5 ${darkMode ? 'text-purple-300/80' : 'text-purple-700/90'}`}>
        <Sparkles size={10} />
        Coach whispers
      </div>
      <div className="flex flex-wrap gap-1.5">
        {signals.map((s) => {
          const Icon = TONE_ICONS[s.tone] || Info;
          const isPro = s.tier === 'pro';
          return (
            <div
              key={s.key}
              className={`text-[11px] px-2 py-1 rounded-md border inline-flex items-start gap-1.5 max-w-full text-left ${TONE_COLORS(s.tone, darkMode, isPro)}`}
            >
              <Icon size={11} className="shrink-0 mt-px" />
              <span className="flex flex-col min-w-0">
                <span className="font-medium leading-snug">{s.label}</span>
                <span className={`text-[10px] leading-snug ${darkMode ? 'opacity-80' : 'opacity-90'}`}>
                  {s.detail}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LiveTradeInsights;
